import { useState, useCallback, type ReactNode } from "react";
import { httpsCallable } from "firebase/functions";
import {
  chatBudgetSubstituteModelId,
  effectiveFallbackModelId,
  lookupModelReasoningSetting,
  parseTokenUsageMaps,
  resolveMonthCost,
  utcDateKey,
  type AdminTokenSummary,
  type AdminMonetizationSummary,
  type AiOperation,
  type ModelCostEntry,
} from "@winche/shared";
import { Badge, Button, Callout, Spinner } from "@winche/ui";
import {
  Block,
  ConfirmDialog,
  EmptyState,
  ErrorMessage,
  Freshness,
  Metric,
  MetricGrid,
  Panel,
  SectionHeader,
  ToneDot,
} from "../components/ui";
import { DataTable, type Column } from "../components/table";
import { compactNum, dateTimeUtc, num, pct, shortDay, toMillis, usd, usdUnit } from "../lib/format";
import { callableFailure, type AdminFailure } from "../lib/callable-error";
import { functions } from "../firebase";

/** Mirrors the per-source entries written to _admin/crawlerHealth by process-crawl-queue. */
export interface CrawlerHealthSource {
  boardId?: string;
  lastSuccessAt?: { seconds: number } | null;
  lastError?: string | null;
  jobsFound?: number;
  /** Market-filtered boards only: postings listed before the filter (WIN-99). */
  jobsListed?: number;
  consecutiveEmptyRuns?: number;
}

export type CrawlerHealthDoc = Record<string, CrawlerHealthSource>;

/** Mirrors _admin/coverageByCountry (coverage-report-weekly.ts, PR-26). */
export interface CoverageByCountryDoc {
  generatedAt?: { seconds: number } | null;
  countries?: Array<{ country: string; countryName: string; active: number; target: number; meetsTarget: boolean }>;
  belowTarget?: string[];
  activeBySource?: Record<string, number>;
  regionalSeed?: {
    size?: number;
    issues?: Array<{ key: string; reason: string; detail: string }>;
    filteredToZero?: Array<{ key: string; detail: string }>;
  };
}

/** Mirrors _admin/systemAlerts (system-alerts.ts). */
export interface AdminSystemAlerts {
  openrouterNoCredits?: {
    detectedAt?: { seconds: number } | null;
    lastNotifiedAt?: { seconds: number } | null;
  };
  pseoRefreshFailure?: {
    reason?: string;
    detail?: string;
    detectedAt?: { seconds: number } | null;
    lastNotifiedAt?: { seconds: number } | null;
  };
  openrouterFreeModelVanished?: {
    previousModelId?: string;
    newModelId?: string;
    configFields?: string;
    detectedAt?: { seconds: number } | null;
    lastNotifiedAt?: { seconds: number } | null;
  };
  aiDailyBudgetExceeded?: {
    spendUsd?: number;
    budgetUsd?: number;
    /** Which line it crossed (q-fn-scheduled-05): Anthropic past its cap plus the margin, the other models past aiDailyBudgetUsd, or both. */
    scope?: "anthropic" | "other" | "both";
    detail?: string;
    detectedAt?: { seconds: number } | null;
    lastNotifiedAt?: { seconds: number } | null;
  };
  openrouterBalanceLow?: {
    balanceUsd?: number;
    thresholdUsd?: number;
    detectedAt?: { seconds: number } | null;
    lastNotifiedAt?: { seconds: number } | null;
  };
  openrouterByokNotUsed?: {
    model?: string;
    detectedAt?: { seconds: number } | null;
    lastNotifiedAt?: { seconds: number } | null;
  };
  /**
   * An account deletion past the retry threshold (account-deletion/orchestrator.ts › stuckDeletion).
   * It goes when that job completes, or names another job that is still stuck.
   */
  accountDeletionStuck?: {
    uid?: string;
    step?: string;
    attempts?: number;
    lastError?: string;
    detectedAt?: { seconds: number } | null;
    lastNotifiedAt?: { seconds: number } | null;
  };
}

/**
 * Mirrors _admin/modelDailyBudget (ai-budget-guard.ts, hourly): today's
 * Anthropic spend against the daily cap and the chat reserve, and since when
 * each lane is paused (ux-admin-04).
 */
export interface AdminModelDailyBudget {
  date?: string;
  spendUsd?: number;
  chatSpendUsd?: number;
  budgetUsd?: number;
  chatReserveUsd?: number;
  backgroundLimitUsd?: number;
  backgroundPaused?: boolean;
  backgroundPausedAt?: { seconds: number } | null;
  chatPaused?: boolean;
  chatPausedAt?: { seconds: number } | null;
  evaluateJobModelId?: string;
  /** False when evaluate_job runs on a model off the Anthropic cap (the Flash Lite kill switch). */
  evaluateJobBudgeted?: boolean;
  updatedAt?: { seconds: number } | null;
}

/** Mirrors the _admin/pseoRefreshRuns doc (current mirror, pseo-daily-refresh). */
export interface AdminPseoRefreshCurrent {
  current?: {
    status?: string;
    at?: { seconds: number } | string | null;
    reason?: string;
    urlCount?: number;
    uploadedFiles?: number;
    versionName?: string;
    opportunities?: number;
    durationMs?: number;
    trigger?: "schedule" | "admin" | string;
    triggeredBy?: string | null;
  };
}

type TriggerPseoRefreshResponse = {
  success: boolean;
  skipped?: boolean;
  reason?: string;
  urlCount?: number;
  durationMs?: number;
};

type AlertKey =
  | "openrouterNoCredits"
  | "pseoRefreshFailure"
  | "openrouterFreeModelVanished"
  | "aiDailyBudgetExceeded"
  | "openrouterBalanceLow"
  | "openrouterByokNotUsed"
  | "accountDeletionStuck";

const PSEO_REFRESH_CALLABLE_TIMEOUT_MS = 540_000;

const triggerPseoRefreshFn = httpsCallable<Record<string, never>, TriggerPseoRefreshResponse>(
  functions,
  "triggerPseoRefresh",
  { timeout: PSEO_REFRESH_CALLABLE_TIMEOUT_MS },
);

function callableErrorMessage(err: unknown): string {
  if (typeof err === "object" && err !== null) {
    const code = "code" in err ? String((err as { code: unknown }).code) : "";
    const message = "message" in err ? String((err as { message: unknown }).message) : "";
    if (code.includes("deadline-exceeded") || /timeout/i.test(message)) {
      return "The refresh is still running on the server (client timeout). The status above updates on its own.";
    }
    if (message) return message;
  }
  return "Could not start the refresh.";
}

interface Props {
  summary: AdminTokenSummary | null;
  monetization: AdminMonetizationSummary | null;
  crawlerHealth: CrawlerHealthDoc | null;
  coverage?: CoverageByCountryDoc | null;
  systemAlerts: AdminSystemAlerts | null;
  pseoRefresh: AdminPseoRefreshCurrent | null;
  reconciling: boolean;
  reconciledJustNow: boolean;
  activeModelId: string | null;
  activeVisionModelId: string | null;
  paidFallbackModelId: string | null;
  paidFallbackVisionModelId: string | null;
  /** `config/global.evaluateJobModelId` (Haiku when unset). */
  evaluateJobModelId?: string | null;
  /** undefined while the listener has not answered; null before aiBudgetGuard published it. */
  modelBudget?: AdminModelDailyBudget | null;
  reasoningEffortByModel?: Record<string, string> | null;
  /** A Firestore listener failed: the freshness chip turns red. */
  listenerFailed?: boolean;
  onClearSystemAlert?: (key: AlertKey) => Promise<void>;
}

/** «14:20 UTC» from a Firestore timestamp. */
function timeUtc(ts: { seconds: number } | null | undefined): string | null {
  const ms = toMillis(ts);
  if (ms == null) return null;
  return `${new Date(ms).toISOString().slice(11, 16)} UTC`;
}

/**
 * "Haiku budget today" (ux-admin-04): the Anthropic cap that stops
 * background work, which the alert budget (aiDailyBudgetUsd) does not. From
 * the hourly mirror `_admin/modelDailyBudget`.
 */
function ModelBudgetCard({
  budget,
  chatSubstituteModelId,
}: {
  budget: AdminModelDailyBudget | null | undefined;
  /** What the chat answers on past the cap: the paid fallback unless it is Anthropic, else Flash Lite. */
  chatSubstituteModelId: string;
}) {
  const today = utcDateKey();
  if (budget === undefined) {
    return <HealthCard title="Haiku budget today" tone="idle" status="Loading" />;
  }
  if (!budget || budget.date !== today) {
    return (
      <HealthCard title="Haiku budget today" tone="idle" status="No report for today yet">
        <p>aiBudgetGuard publishes it every hour (minute 7, UTC).</p>
      </HealthCard>
    );
  }
  const spend = budget.spendUsd ?? 0;
  const limit = budget.backgroundLimitUsd ?? null;
  const pausedSince = timeUtc(budget.backgroundPausedAt);
  const chatSince = timeUtc(budget.chatPausedAt);
  const tone = budget.backgroundPaused ? "bad" : limit != null && limit > 0 && spend >= limit * 0.8 ? "warn" : "ok";
  return (
    <HealthCard
      title="Haiku budget today"
      tone={tone}
      status={
        budget.backgroundPaused
          ? pausedSince
            ? `Background paused since ${pausedSince}`
            : "Background paused"
          : `${usd(spend)} of ${usd(limit)} for background`
      }
    >
      <p className="tabular-nums">
        {usd(spend)} spent of {usd(budget.budgetUsd)} · chat {usd(budget.chatSpendUsd)}
      </p>
      <p className="tabular-nums">
        Background stops at {usd(limit)}; the last {usd(budget.chatReserveUsd)} stay for the chat.
      </p>
      {budget.backgroundPaused ? <p className="text-danger-fg">Evaluations and other background work wait for the next UTC day.</p> : null}
      {budget.chatPaused ? (
        <p className="text-warning-fg">
          Chat answers on <span className="font-mono">{chatSubstituteModelId}</span>
          {chatSince ? ` since ${chatSince}` : ""}.
        </p>
      ) : null}
      {budget.evaluateJobBudgeted === false ? (
        <p>
          evaluate_job runs on <span className="font-mono">{budget.evaluateJobModelId}</span>, off the cap.
        </p>
      ) : null}
      <p className="text-subtle-foreground">Updated {timeUtc(budget.updatedAt) ?? "—"} · aiBudgetGuard, hourly</p>
    </HealthCard>
  );
}

function reasoningLabel(modelId: string | null, map: Record<string, string> | null | undefined): string | null {
  if (!modelId) return null;
  const setting = lookupModelReasoningSetting(modelId, map ?? {});
  if (setting === "none") return "reasoning off";
  if (setting === "auto") return "reasoning auto";
  return `reasoning ${setting}`;
}

function currentMonthKey() {
  return new Date().toISOString().slice(0, 7); // "YYYY-MM"
}

function finiteCost(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

/** "google_gemini-2.5-flash-lite_free" → "google/gemini-2.5-flash-lite:free". */
function decodeModelKey(key: string): string {
  const isFreeTier = key.endsWith("_free");
  const base = isFreeTier ? key.slice(0, -5) : key;
  const readable = base.replace(/^([^_]+)_/, "$1/");
  return isFreeTier ? `${readable}:free` : readable;
}

// ── Crawler health semaphore ────────────────────────────────────────────────
// Red (down) is reserved for real failures surfaced by the crawler (lastError:
// board 404, blocked/429, all-keywords-failed, timeout in a few cases). A
// reachable board that returns 0 jobs is EMPTY, not broken — it stays amber
// (2026-08-26: greenhouse__wholesail with 7 empty runs was flagged red).
type SourceStatus = "ok" | "warn" | "down";

const EMPTY_RUNS_AMBER = 3;
const STALE_AMBER_HOURS = 48;
// A market-filtered board that listed postings, none in its countries, is
// filtered to zero (WIN-99): live, not empty. The queue still backs it off
// like an empty listing (up to 7 days), so it is stale only past that.
const FILTERED_STALE_AMBER_HOURS = 7 * 24 + STALE_AMBER_HOURS;

function sourceStatus(entry: CrawlerHealthSource | null | undefined): { status: SourceStatus; reason: string | null } {
  if (!entry || typeof entry !== "object") return { status: "down", reason: "invalid health entry" };
  const emptyRuns = entry.consecutiveEmptyRuns ?? 0;
  if (entry.lastError) {
    // Transient fetch timeouts self-heal on the next successful crawl: warn
    // (amber), not down (red), to avoid false alarms on the semaphore.
    if (/timeout|aborted due to timeout/i.test(entry.lastError)) {
      return { status: "warn", reason: entry.lastError };
    }
    return { status: "down", reason: entry.lastError };
  }
  if (emptyRuns >= EMPTY_RUNS_AMBER) {
    return { status: "warn", reason: `${emptyRuns} consecutive empty runs (board live, 0 jobs)` };
  }
  if (!entry.lastSuccessAt?.seconds) return { status: "down", reason: "never succeeded" };
  const staleHours = (Date.now() - entry.lastSuccessAt.seconds * 1000) / 3_600_000;
  const filteredToZero = entry.jobsFound === 0 && (entry.jobsListed ?? 0) > 0;
  if (staleHours >= (filteredToZero ? FILTERED_STALE_AMBER_HOURS : STALE_AMBER_HOURS)) {
    return { status: "warn", reason: `stale ${Math.floor(staleHours)}h` };
  }
  return { status: "ok", reason: null };
}

const STATUS_TONE = { ok: "ok", warn: "warn", down: "bad" } as const;

/** One card of the system-health grid: a status dot, a title, a line and details. */
function HealthCard({
  title,
  tone,
  status,
  children,
  action,
}: {
  title: string;
  tone: "ok" | "warn" | "bad" | "idle";
  status: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <Panel className="flex min-w-0 flex-col">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-caption text-muted-foreground">{title}</p>
          <p className="mt-1 flex items-center gap-2 text-sm font-medium text-foreground">
            <ToneDot tone={tone} />
            {status}
          </p>
        </div>
        {action}
      </div>
      {children ? <div className="mt-3 space-y-1 text-caption text-muted-foreground">{children}</div> : null}
    </Panel>
  );
}

function CrawlerHealthCard({ health }: { health: CrawlerHealthDoc | null }) {
  if (!health || typeof health !== "object" || Object.keys(health).length === 0) {
    return (
      <HealthCard title="Crawlers" tone="idle" status="No report yet">
        <p>Populated by the crawl queue worker.</p>
      </HealthCard>
    );
  }
  const evaluated = Object.entries(health).map(([source, entry]) => ({ source, ...sourceStatus(entry) }));
  const ok = evaluated.filter((e) => e.status === "ok").length;
  const warn = evaluated.filter((e) => e.status === "warn").length;
  const down = evaluated.filter((e) => e.status === "down").length;
  const overall: SourceStatus = down > 0 ? "down" : warn > 0 ? "warn" : "ok";
  const problems = evaluated.filter((e) => e.status !== "ok").slice(0, 4);
  return (
    <HealthCard
      title="Crawlers"
      tone={STATUS_TONE[overall]}
      status={overall === "down" ? "Issues detected" : overall === "warn" ? "Degraded" : "Healthy"}
    >
      <p className="tabular-nums">
        {evaluated.length} sources · {ok} ok · {warn} warn · {down} down
      </p>
      {problems.map((p) => (
        <p key={p.source} className="truncate" title={`${p.source} — ${p.reason}`}>
          <span className={p.status === "down" ? "font-mono text-danger-fg" : "font-mono text-warning-fg"}>{p.source}</span>{" "}
          — {p.reason}
        </p>
      ))}
      {down + warn > problems.length && <p>and {down + warn - problems.length} more</p>}
    </HealthCard>
  );
}

function CoverageCard({ coverage }: { coverage: CoverageByCountryDoc | null }) {
  const countries = coverage?.countries ?? [];
  const issues = coverage?.regionalSeed?.issues ?? [];
  const filteredToZero = coverage?.regionalSeed?.filteredToZero ?? [];
  if (countries.length === 0) {
    return (
      <HealthCard title="Coverage by country" tone="idle" status="No data yet">
        <p>coverageReportWeekly computes it on Mondays.</p>
      </HealthCard>
    );
  }
  const below = countries.filter((c) => !c.meetsTarget);
  return (
    <HealthCard
      title="Coverage by country"
      tone={below.length > 0 ? "warn" : "ok"}
      status={below.length > 0 ? `${below.length} below target` : "All markets on target"}
    >
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        {countries.map((c) => (
          <p key={c.country} className="flex justify-between gap-2">
            <span className="truncate">{c.countryName}</span>
            <span className={c.meetsTarget ? "tabular-nums text-foreground" : "tabular-nums text-warning-fg"}>
              {compactNum(c.active)} / {compactNum(c.target)}
            </span>
          </p>
        ))}
      </div>
      <p>
        ES/LATAM seed: {coverage?.regionalSeed?.size ?? 0} companies, {issues.length} to review
        {filteredToZero.length > 0 ? `, ${filteredToZero.length} with no postings in their markets` : ""}
        {coverage?.generatedAt ? ` · ${shortDay(coverage.generatedAt)}` : ""}
      </p>
    </HealthCard>
  );
}

export function OverviewSection({
  summary,
  monetization,
  crawlerHealth,
  coverage,
  systemAlerts,
  pseoRefresh,
  reconciling,
  reconciledJustNow,
  activeModelId,
  activeVisionModelId,
  paidFallbackModelId,
  paidFallbackVisionModelId,
  evaluateJobModelId,
  modelBudget,
  reasoningEffortByModel,
  listenerFailed = false,
  onClearSystemAlert,
}: Props) {
  const [clearingAlert, setClearingAlert] = useState<AlertKey | null>(null);
  const [alertError, setAlertError] = useState<AdminFailure | null>(null);
  const [triggeringPseo, setTriggeringPseo] = useState(false);
  const [confirmPseo, setConfirmPseo] = useState(false);
  const [pseoTriggerMsg, setPseoTriggerMsg] = useState<string | null>(null);
  const [pseoTriggerError, setPseoTriggerError] = useState<string | null>(null);

  const pseoCurrent = pseoRefresh?.current;
  const pseoRunning = triggeringPseo || pseoCurrent?.status === "running";

  const handleTriggerPseo = useCallback(async () => {
    setTriggeringPseo(true);
    setPseoTriggerMsg(null);
    setPseoTriggerError(null);
    try {
      const res = await triggerPseoRefreshFn({});
      if (res.data.skipped) {
        setPseoTriggerMsg("A refresh is already running.");
      } else if (!res.data.success) {
        setPseoTriggerError(res.data.reason ? `Aborted: ${res.data.reason}` : "The refresh aborted.");
      } else {
        setPseoTriggerMsg(res.data.urlCount != null ? `Done — ${num(res.data.urlCount)} URLs.` : "Done.");
      }
    } catch (err) {
      setPseoTriggerError(callableErrorMessage(err));
    } finally {
      setTriggeringPseo(false);
    }
  }, []);

  async function handleClearAlert(key: AlertKey) {
    if (!onClearSystemAlert) return;
    setClearingAlert(key);
    setAlertError(null);
    // The alert stays: say why instead of a silent unhandled rejection (ux-admin-09).
    setAlertError(await callableFailure("clearAdminSystemAlert", () => onClearSystemAlert(key)));
    setClearingAlert(null);
  }

  const opCounts: { op: string; count: number }[] = summary?.operationCounts
    ? (Object.entries(summary.operationCounts) as [AiOperation, number | undefined][])
        .filter((e): e is [AiOperation, number] => e[1] != null)
        .map(([op, count]) => ({ op, count }))
    : [];

  // Monetization derived metrics
  const mrr = monetization?.mrrUsd ?? 0;
  const totalUsers = (monetization?.freeCount ?? 0) + (monetization?.proSearchCount ?? 0) + (monetization?.proRadarCount ?? 0);
  const proCount = (monetization?.proSearchCount ?? 0) + (monetization?.proRadarCount ?? 0);
  const conversionPct = totalUsers > 0 ? (proCount / totalUsers) * 100 : null;

  // AI cost this month
  const monthKey = currentMonthKey();
  const parsedCosts = summary ? parseTokenUsageMaps(summary as unknown as Record<string, unknown>) : null;
  const monthCost = resolveMonthCost(parsedCosts?.monthlyAiCost, monthKey);
  const monthIncomplete = monthCost.completeness !== "complete";

  const mau = summary?.mauCount;
  const userCount = summary?.userCount;
  const retentionPct = mau != null && userCount && userCount > 0 ? (mau / userCount) * 100 : null;
  const hasMonetization = monetization != null;
  const hasSummary = summary != null;

  const updatedAt = Math.max(
    toMillis(summary?.lastUpdatedAt) ?? 0,
    toMillis((monetization as unknown as { lastUpdatedAt?: unknown } | null)?.lastUpdatedAt) ?? 0,
  );

  // ── Alerts: only the ones that exist, first thing on the page.
  const alerts: { key: AlertKey; tone: "danger" | "warning"; title: string; detail: ReactNode; at: unknown }[] = [];
  if (systemAlerts?.openrouterNoCredits) {
    alerts.push({
      key: "openrouterNoCredits",
      tone: "danger",
      title: "OpenRouter is out of credits",
      detail: "AI calls fail until the balance is topped up.",
      at: systemAlerts.openrouterNoCredits.detectedAt,
    });
  }
  if (systemAlerts?.aiDailyBudgetExceeded) {
    alerts.push({
      key: "aiDailyBudgetExceeded",
      tone: "danger",
      title: "Daily AI spend over its alert",
      detail:
        systemAlerts.aiDailyBudgetExceeded.detail ??
        `${usd(systemAlerts.aiDailyBudgetExceeded.spendUsd)} spent of ${usd(systemAlerts.aiDailyBudgetExceeded.budgetUsd)}.`,
      at: systemAlerts.aiDailyBudgetExceeded.detectedAt,
    });
  }
  if (systemAlerts?.openrouterBalanceLow) {
    alerts.push({
      key: "openrouterBalanceLow",
      tone: "warning",
      title: "OpenRouter balance is low",
      detail: `${usd(systemAlerts.openrouterBalanceLow.balanceUsd)} left (threshold ${usd(systemAlerts.openrouterBalanceLow.thresholdUsd)}).`,
      at: systemAlerts.openrouterBalanceLow.detectedAt,
    });
  }
  if (systemAlerts?.openrouterByokNotUsed) {
    alerts.push({
      key: "openrouterByokNotUsed",
      tone: "warning",
      title: "Anthropic calls billed on OpenRouter credits",
      detail: (
        <>
          <span className="font-mono">{systemAlerts.openrouterByokNotUsed.model ?? "anthropic/*"}</span> answered without
          BYOK. Check the Anthropic key and "Always use this key" in OpenRouter.
        </>
      ),
      at: systemAlerts.openrouterByokNotUsed.detectedAt,
    });
  }
  if (systemAlerts?.accountDeletionStuck) {
    const a = systemAlerts.accountDeletionStuck;
    alerts.push({
      key: "accountDeletionStuck",
      tone: "danger",
      title: "An account deletion is stuck",
      detail: (
        <>
          <span className="font-mono">accountDeletionJobs/{a.uid ?? "?"}</span> at{" "}
          <span className="font-mono">{a.step ?? "?"}</span> after {a.attempts ?? "?"} attempts
          {a.lastError ? <>: <span className="font-mono">{a.lastError}</span></> : null}. The cron keeps retrying.
        </>
      ),
      at: a.detectedAt,
    });
  }
  if (systemAlerts?.pseoRefreshFailure) {
    alerts.push({
      key: "pseoRefreshFailure",
      tone: "warning",
      title: "pSEO refresh failed",
      detail: <span className="font-mono">{systemAlerts.pseoRefreshFailure.reason ?? "unknown reason"}</span>,
      at: systemAlerts.pseoRefreshFailure.detectedAt,
    });
  }
  if (systemAlerts?.openrouterFreeModelVanished) {
    const a = systemAlerts.openrouterFreeModelVanished;
    alerts.push({
      key: "openrouterFreeModelVanished",
      tone: "warning",
      title: "The free model vanished",
      detail: (
        <span className="font-mono">
          {a.previousModelId ?? "?"} → {a.newModelId ?? "openrouter/free"}
        </span>
      ),
      at: a.detectedAt,
    });
  }

  // ── Cost by model
  const modelCosts = parsedCosts?.modelCosts ?? summary?.modelCosts;
  const modelRows = modelCosts
    ? Object.entries(modelCosts as Record<string, Partial<ModelCostEntry> | null>).flatMap(([key, entry]) => {
        // Prefer the parsed map: a Firestore path split on `.` in ids like
        // `gemini-2.5` used to yield `{ callCount: undefined }`.
        if (!entry || typeof entry !== "object") return [];
        const row = {
          key,
          id: decodeModelKey(key),
          isFree: key.endsWith("_free"),
          callCount: finiteCost(entry.callCount),
          costUsd: finiteCost(entry.costUsd),
          promptTokens: finiteCost(entry.promptTokens),
          completionTokens: finiteCost(entry.completionTokens),
          fallbackCount: finiteCost(entry.fallbackCount),
        };
        const empty = !row.callCount && !row.costUsd && !row.promptTokens && !row.completionTokens && !row.fallbackCount;
        return empty ? [] : [row];
      })
    : [];
  type ModelRow = (typeof modelRows)[number];
  const totalCalls = modelRows.reduce((s, e) => s + e.callCount, 0);
  const totalCost = modelRows.reduce((s, e) => s + e.costUsd, 0);
  const totalFallbacks = modelRows.reduce((s, e) => s + e.fallbackCount, 0);

  const modelColumns: Column<ModelRow>[] = [
    {
      key: "model",
      header: "Model",
      cell: (r) => (
        <span className="flex items-center gap-1.5">
          <span className="font-mono">{r.id}</span>
          {r.isFree && (
            <Badge tone="success" size="sm">
              free
            </Badge>
          )}
        </span>
      ),
      sortValue: (r) => r.id,
      footer: "Total",
    },
    { key: "calls", header: "Calls", align: "right", cell: (r) => num(r.callCount), sortValue: (r) => r.callCount, footer: num(totalCalls) },
    {
      key: "share",
      header: "Share",
      align: "right",
      cell: (r) => <span className="text-muted-foreground">{totalCalls > 0 ? pct((r.callCount / totalCalls) * 100) : "—"}</span>,
      sortValue: (r) => r.callCount,
    },
    {
      key: "fallbacks",
      header: "Fallbacks",
      align: "right",
      title: "Free-tier calls that fell back to the paid model",
      cell: (r) =>
        r.isFree ? (
          <span className={r.fallbackCount > 0 ? "text-danger-fg" : undefined}>
            {num(r.fallbackCount)}
            {r.fallbackCount > 0 && r.callCount > 0 ? ` (${pct((r.fallbackCount / r.callCount) * 100)})` : ""}
          </span>
        ) : (
          <span className="text-subtle-foreground">—</span>
        ),
      sortValue: (r) => r.fallbackCount,
      footer: num(totalFallbacks),
    },
    { key: "prompt", header: "Prompt tokens", align: "right", cell: (r) => compactNum(r.promptTokens), sortValue: (r) => r.promptTokens },
    {
      key: "completion",
      header: "Completion tokens",
      align: "right",
      cell: (r) => compactNum(r.completionTokens),
      sortValue: (r) => r.completionTokens,
    },
    { key: "cost", header: "Cost", align: "right", cell: (r) => usd(r.costUsd), sortValue: (r) => r.costUsd, footer: usd(totalCost) },
    {
      key: "avg",
      header: "Avg / call",
      align: "right",
      cell: (r) => <span className="text-muted-foreground">{usdUnit(r.callCount > 0 ? r.costUsd / r.callCount : null)}</span>,
      sortValue: (r) => (r.callCount > 0 ? r.costUsd / r.callCount : null),
    },
  ];

  // Effective routing (ux-admin-04): an unset fallback is Flash Lite, not hidden.
  const textFallback = effectiveFallbackModelId(activeModelId, paidFallbackModelId);
  const visionFallback = effectiveFallbackModelId(activeVisionModelId, paidFallbackVisionModelId);
  const routing: { label: string; id: string | null; note: string | null; reasoning: string | null }[] = activeModelId
    ? [
        { label: "Text", id: activeModelId, note: null, reasoning: reasoningLabel(activeModelId, reasoningEffortByModel) },
        { label: "Text fallback", id: textFallback, note: paidFallbackModelId ? null : "default", reasoning: null },
        { label: "Vision", id: activeVisionModelId, note: null, reasoning: null },
        { label: "Vision fallback", id: visionFallback, note: paidFallbackVisionModelId ? null : "default", reasoning: null },
        { label: "evaluate_job", id: evaluateJobModelId ?? null, note: "own model", reasoning: null },
      ].filter((r) => r.id)
    : [];

  const pseoTone =
    pseoCurrent?.status === "ok" ? "ok" : pseoCurrent?.status === "running" ? "warn" : !pseoCurrent ? "idle" : "bad";

  return (
    <div>
      <SectionHeader
        title="Overview"
        subtitle="MRR and subscription counts update on each sale or cancellation."
        meta={
          <Freshness
            at={updatedAt || null}
            failed={listenerFailed}
            source="_admin/tokenUsageSummary · _admin/monetizationSummary"
          />
        }
      />

      {reconciling && (
        <Callout tone="info" className="mb-4" icon={<Spinner size="sm" />} live="polite">
          Calculating subscription counts and MRR for the first time today.
        </Callout>
      )}
      {!reconciling && reconciledJustNow && (
        <Callout tone="success" className="mb-4" live="polite">
          Monetization data refreshed for today.
        </Callout>
      )}

      {/* 1) Alerts first, only when there are any. */}
      {alertError && <ErrorMessage failure={alertError} />}
      {alerts.length > 0 && (
        <div className="mb-6 space-y-2" role="region" aria-label="System alerts">
          {alerts.map((a) => (
            <Callout
              key={a.key}
              tone={a.tone}
              title={a.title}
              action={
                onClearSystemAlert ? (
                  <Button
                    variant="outline"
                    size="sm"
                    loading={clearingAlert === a.key}
                    disabled={clearingAlert !== null && clearingAlert !== a.key}
                    onClick={() => void handleClearAlert(a.key)}
                  >
                    Dismiss
                  </Button>
                ) : undefined
              }
            >
              {a.detail} <span className="text-muted-foreground">Detected {dateTimeUtc(a.at)}.</span>
            </Callout>
          ))}
        </div>
      )}

      {/* 2) Main KPIs */}
      <MetricGrid cols={4} className="mb-3">
        <Metric
          label="List MRR"
          value={hasMonetization ? usd(mrr) : "—"}
          hint="Catalog price × active PRO headcount, not Lemon Squeezy cash."
        />
        <Metric
          label={`AI cost · ${monthKey}`}
          value={hasSummary ? usd(monthCost.value) : "—"}
          sub={monthIncomplete ? "Month in progress" : undefined}
          hint="Observed cost for the UTC month. A missing month is —, never $0."
        />
        <Metric label="Active users (30d)" value={mau != null ? num(mau) : "—"} sub={retentionPct != null ? `${pct(retentionPct)} of all users` : undefined} />
        <Metric
          label="Conversion"
          value={hasMonetization ? pct(conversionPct) : "—"}
          tone={conversionPct != null && conversionPct >= 5 ? "good" : "default"}
          hint="PRO users / all users. Target: 5%."
        />
      </MetricGrid>
      <MetricGrid cols={4} className="mb-8">
        <Metric label="Users" value={hasMonetization ? num(totalUsers) : num(userCount)} />
        <Metric
          label="PRO users"
          value={hasMonetization ? num(proCount) : "—"}
          sub={hasMonetization ? `${num(monetization!.proSearchCount)} Search · ${num(monetization!.proRadarCount)} Radar` : undefined}
        />
        <Metric
          label="Active / all users"
          value={pct(retentionPct)}
          tone={retentionPct != null && retentionPct >= 30 ? "good" : "default"}
          hint="Active in the last 30 days / all users. Target: 30%."
        />
        <Metric label="Lifetime AI cost" value={hasSummary ? usd(summary?.totalEstimatedCostUsd ?? 0) : "—"} />
      </MetricGrid>

      {/* 3) System health */}
      <Block title="System health">
        <div className="grid grid-cols-1 gap-3 @3xl:grid-cols-2 @6xl:grid-cols-4">
          <ModelBudgetCard budget={modelBudget} chatSubstituteModelId={chatBudgetSubstituteModelId(paidFallbackModelId)} />
          <HealthCard
            title="Daily pSEO refresh"
            tone={pseoTone}
            status={
              !pseoCurrent
                ? "Never run"
                : pseoCurrent.status === "running"
                  ? "Running"
                  : pseoCurrent.status === "ok"
                    ? `${num(pseoCurrent.urlCount ?? 0)} URLs`
                    : "Failed"
            }
            action={
              <Button variant="outline" size="sm" loading={pseoRunning} onClick={() => setConfirmPseo(true)}>
                Run now
              </Button>
            }
          >
            {pseoCurrent ? (
              <p>
                {dateTimeUtc(pseoCurrent.at)}
                {pseoCurrent.status === "ok" ? ` · ${num(pseoCurrent.uploadedFiles ?? 0)} files uploaded` : ""}
                {pseoCurrent.trigger === "admin" ? " · manual" : ""}
              </p>
            ) : null}
            {pseoCurrent?.status !== "ok" && pseoCurrent?.status !== "running" && pseoCurrent?.reason ? (
              <p className="font-mono text-danger-fg">{pseoCurrent.reason}</p>
            ) : null}
            {pseoTriggerMsg && <p className="text-foreground">{pseoTriggerMsg}</p>}
            {pseoTriggerError && <p className="text-danger-fg">{pseoTriggerError}</p>}
          </HealthCard>
          <CrawlerHealthCard health={crawlerHealth} />
          <CoverageCard coverage={coverage ?? null} />
        </div>
      </Block>

      {/* 4) Detail */}
      <Block
        title="AI routing"
        hint="Read-only. Text and vision models in Settings; evaluate_job's model is config/global.evaluateJobModelId (pnpm ops:cost-controls)."
      >
        {routing.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {routing.map((r) => (
              <span key={r.label} className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-label">
                <span className="text-muted-foreground">{r.label}</span>
                <span className="font-mono">{r.id}</span>
                {r.note ? (
                  <Badge tone="neutral" size="sm">
                    {r.note}
                  </Badge>
                ) : null}
                {r.reasoning ? (
                  <Badge tone="outline" size="sm">
                    {r.reasoning}
                  </Badge>
                ) : null}
              </span>
            ))}
          </div>
        ) : (
          <EmptyState message="Model config not loaded." />
        )}
      </Block>

      <Block title="AI tokens">
        <MetricGrid cols={3}>
          <Metric label="Total tokens" value={compactNum(summary?.totalTokens)} />
          <Metric label="Prompt tokens" value={compactNum(summary?.totalPromptTokens)} />
          <Metric label="Completion tokens" value={compactNum(summary?.totalCompletionTokens)} />
        </MetricGrid>
      </Block>

      {modelRows.length > 0 && (
        <Block title="Cost by model" hint="Real costs reported by OpenRouter, lifetime.">
          <DataTable
            caption="Cost by model"
            columns={modelColumns}
            rows={modelRows}
            rowKey={(r) => r.key}
            defaultSort={{ key: "cost", dir: "desc" }}
          />
        </Block>
      )}

      <Block title="AI operations">
        {opCounts.length > 0 ? (
          <DataTable
            caption="AI operations"
            columns={[
              { key: "op", header: "Operation", cell: (r) => <span className="font-mono">{r.op}</span>, sortValue: (r) => r.op },
              { key: "count", header: "Calls", align: "right", cell: (r) => num(r.count), sortValue: (r) => r.count },
            ]}
            rows={opCounts}
            rowKey={(r) => r.op}
            defaultSort={{ key: "count", dir: "desc" }}
          />
        ) : (
          <EmptyState message={hasSummary ? "No AI operations recorded yet." : "No AI usage summary yet. It appears after the first scan."} />
        )}
      </Block>

      <ConfirmDialog
        open={confirmPseo}
        onOpenChange={setConfirmPseo}
        title="Run the pSEO refresh now?"
        scope="Regenerates every pSEO page and publishes a new Hosting release for winche.ai."
        confirmLabel="Publish refresh"
        onConfirm={() => {
          void handleTriggerPseo();
        }}
      />
    </div>
  );
}
